'use client';

/**
 * Web-side referral attribution. The user's referral link is
 * `${origin}/?ref=<code>`; capturing the code on landing lets the OAuth
 * signup flow attach `referredById` to the new user even though
 * Yandex/VK/Telegram redirects strip the original query string.
 *
 * Persisted in localStorage with a 30-day TTL — long enough to cover a
 * "I'll sign up later" gap, short enough that an old code doesn't haunt
 * the device forever.
 */

const REFERRAL_KEY = 'mix_pending_ref';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredRef {
  code: string;
  savedAt: number;
}

/** Reads `?ref=` from the current URL, persists it (with timestamp), and
 * strips it from the address bar so the user doesn't accidentally share
 * someone else's code by copy-pasting their location. Safe to call
 * repeatedly: a missing/empty `?ref` is a no-op, and the latest code
 * always wins. */
export function captureReferralFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const code = url.searchParams.get('ref');
  if (!code) return;
  try {
    window.localStorage.setItem(
      REFERRAL_KEY,
      JSON.stringify({ code, savedAt: Date.now() } satisfies StoredRef),
    );
  } catch {
    // Quota / private mode — silent.
  }
  url.searchParams.delete('ref');
  const search = url.searchParams.toString();
  const clean = url.pathname + (search ? `?${search}` : '') + url.hash;
  window.history.replaceState(null, '', clean);
}

/** Returns the stored code if present and not expired; clears stale
 * entries as a side effect so the next call sees a clean slate. */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(REFERRAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredRef>;
    if (
      typeof parsed.code !== 'string' ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > TTL_MS
    ) {
      window.localStorage.removeItem(REFERRAL_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
}

/** Drop the stored code after it's been applied (or after we know
 * applying it is no longer relevant). */
export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REFERRAL_KEY);
  } catch {
    // ignore
  }
}
